import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { FincasModel } from 'src/app/models/form-common/fincas.module';
import {
  CueCounter,
  CueErrorPayload,
  CueHistory,
  CueMessagePayload,
  CueMessageState,
  CueSendResult,
  CueStatusResponse,
  CueSyncActivity,
  CueSyncResponse,
  CueUserWarning,
} from 'src/app/models/fincas/cue.model';
import { CueExportService } from 'src/app/service/fincas/cue-export.service';
import { CueErrorPresenter } from '../presenters/cue-error.presenter';
import { CueHistoryPresenter } from '../presenters/cue-history.presenter';
import { CueStatusPresenter } from '../presenters/cue-status.presenter';
import { CueSummaryPresenter } from '../presenters/cue-summary.presenter';

const CUE_POLL_EVERY_MS = Number('8000');
const CUE_MAX_POLL_ATTEMPTS = Number('60');

/** Coordinates the CUE export flow and keeps the component focused on presentation. */
@Injectable()
export class CueExportFlowFacade implements OnDestroy {
  public counter: CueCounter = { fito: null, ferti: null };

  public cueHistory: CueHistory[] = [];

  public importResult: CueMessagePayload | null = null;

  public messageState: CueMessageState = '';

  public showSpinner = false;

  public statusCue: CueHistory | null = null;

  public userWarnings: CueUserWarning[] = [];

  private readonly destroy$ = new Subject<void>();

  private activeCuePollCode: string | null = null;

  private lastPendingPollLimitReachedCode: string | null = null;

  private pollRunId = 0;

  public constructor(private readonly cueExportService: CueExportService) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public reset(): void {
    this.cancelCuePolling();
    this.messageState = '';
    this.importResult = null;
    this.cueHistory = [];
    this.statusCue = null;
    this.counter = { fito: null, ferti: null };
    this.userWarnings = [];
    this.activeCuePollCode = null;
    this.lastPendingPollLimitReachedCode = null;
  }

  public close(): void {
    this.cancelCuePolling();
    this.messageState = '';
  }

  public pendingTaskCount(): number {
    return CueSummaryPresenter.pendingTaskCount(this.counter);
  }

  public checkStatus(finca: FincasModel, refreshRequested: () => void): void {
    this.cueExportService
      .checkStatus(finca)
      .pipe(
        catchError((error: unknown) => {
          console.error('Error checking CUE status', error);

          return of({
            statusCode: HttpStatusCode.InternalServerError,
            data: { history: [] }
          } as CueStatusResponse);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => this.applyStatusResponse(response, finca, refreshRequested));
  }

  public canSendCue(): boolean {
    return !this.showSpinner && !this.hasPendingCue() && CueSummaryPresenter.pendingTaskCount(this.counter) > 0;
  }

  public hasPendingCue(): boolean {
    return this.statusCue !== null && CueStatusPresenter.isPendingStatus(this.statusCue.estado);
  }

  public retryPendingCue(finca: FincasModel, refreshRequested: () => void): void {
    if (!this.statusCue) {
      return;
    }

    const petitionCode = CueHistoryPresenter.historyCode(this.statusCue);

    if (!petitionCode || petitionCode === '-') {
      return;
    }

    this.showSpinner = true;
    this.lastPendingPollLimitReachedCode = null;
    void this.pollCueVerification(petitionCode, finca, refreshRequested);
  }

  public send(finca: FincasModel | null, refreshRequested: () => void): void {
    if (!finca) {
      this.userWarnings = [];
      this.setMessage('error', {
        statusCode: HttpStatusCode.BadRequest,
        statusMessage: 'No se ha seleccionado ninguna finca.'
      });
      return;
    }

    this.showSpinner = true;
    this.lastPendingPollLimitReachedCode = null;
    void this.exportCue(finca, refreshRequested);
  }

  private applyStatusResponse(response: CueStatusResponse, finca: FincasModel, refreshRequested: () => void): void {
    this.counter = {
      ferti: response.data?.task_ferti ?? null,
      fito: response.data?.task_fito ?? null
    };

    if (response.statusCode !== HttpStatusCode.Ok || !response.data?.history?.length) {
      this.userWarnings = [];
      return;
    }

    this.statusCue = response.data.history.at(0) ?? null;
    this.cueHistory = response.data.history;
    this.userWarnings = CueHistoryPresenter.userWarnings(this.statusCue);
    this.syncLatestPendingCue(finca, refreshRequested);
  }

  private async exportCue(finca: FincasModel, refreshRequested: () => void): Promise<void> {
    this.messageState = '';

    if (!this.hasRequiredCueData(finca)) {
      this.setMessage('error', {
        statusCode: HttpStatusCode.BadRequest,
        statusMessage: 'Faltan datos para preparar el cuaderno',
        data: [
          {
            body: 'Revisa la finca antes de enviar. No se ha encontrado el identificador de la finca.'
          }
        ]
      });
      return;
    }

    try {
      const response = await firstValueFrom(
        this.cueExportService.send(finca).pipe(
          tap((httpResponse) => {
            if (httpResponse.status === HttpStatusCode.NoContent) {
              this.setMessage('warn', {
                statusCode: httpResponse.status,
                statusMessage: 'No hay datos preparados para enviar.',
                data: [
                  {
                    body: 'La finca no tiene tratamientos ni fertilizaciones preparadas para enviar al CUE.'
                  }
                ]
              });
            }
          }),
          catchError((error) => this.handleCueSendError(error, finca, refreshRequested))
        )
      );

      await this.handleCueResponse(response.body ?? {}, finca, refreshRequested);
    } catch (error) {
      console.error('Unexpected CUE export error', error);
      this.setMessage('error', {
        statusCode: HttpStatusCode.InternalServerError,
        statusMessage: 'Servicio no disponible. Contacta con el administrador.'
      });
    }
  }

  private hasRequiredCueData(finca: FincasModel): boolean {
    return finca.id != null;
  }

  private handleCueSendError(
    error: { status?: number; error?: CueErrorPayload },
    finca: FincasModel,
    refreshRequested: () => void,
  ): Observable<HttpResponse<CueSendResult>> {
    this.showSpinner = false;
    const errorBody = error.error;
    const details = CueErrorPresenter.formatErrors(errorBody);
    this.userWarnings = CueErrorPresenter.warningsFromPayload(errorBody);

    if (error.status === HttpStatusCode.Conflict) {
      const pendingCode = errorBody?.data?.codigo_peticion ?? errorBody?.data?.codigo ?? '';
      const isPendingConflict = (errorBody?.data?.errorCode ?? errorBody?.errorCode) === 'CUE_PENDING_PETITION_EXISTS';
      const messageState: CueMessageState = isPendingConflict ? 'warn' : 'error';
      const conflictTitle = isPendingConflict
        ? 'Ya hay un cuaderno enviado y pendiente de respuesta.'
        : 'El cuaderno ya consta como enviado.';
      let conflictBody = (errorBody?.data?.descripcion ?? '') + '. Contacta con el administrador.';

      if (isPendingConflict) {
        conflictBody = 'Retomamos la comprobación del envío pendiente.';
      }

      this.setMessage(messageState, {
        statusCode: HttpStatusCode.Conflict,
        statusMessage: conflictTitle,
        data: [
          {
            body: conflictBody
          }
        ]
      });

      if (isPendingConflict && pendingCode) {
        this.showSpinner = true;
        void this.pollCueVerification(pendingCode, finca, refreshRequested);
      }

      return of(
        new HttpResponse<CueSendResult>({
          body: {} as CueSendResult,
          status: error.status ?? 0
        })
      );
    }

    if (error.status === HttpStatusCode.UnprocessableEntity) {
      this.setMessage('error', {
        statusCode: HttpStatusCode.UnprocessableEntity,
        statusMessage: 'No hemos podido enviar el cuaderno.',
        data: [{ body: details }]
      });

      return of(
        new HttpResponse<CueSendResult>({
          body: {} as CueSendResult,
          status: error.status ?? 0
        })
      );
    }

    if (error.status === HttpStatusCode.InternalServerError) {
      this.setMessage('error', {
        statusCode: HttpStatusCode.InternalServerError,
        statusMessage: 'Servicio no disponible. Contacta con el administrador.',
        data: [{ body: details }]
      });

      return of(
        new HttpResponse<CueSendResult>({
          body: {} as CueSendResult,
          status: error.status ?? 0
        })
      );
    }

    return throwError(() => error);
  }

  private async handleCueResponse(body: CueSendResult, finca: FincasModel, refreshRequested: () => void): Promise<void> {
    if (body.local_exception || body.message || body.errors?.length) {
      this.userWarnings = CueErrorPresenter.warningsFromPayload(body);
      this.setMessage('error', {
        statusCode: HttpStatusCode.UnprocessableEntity,
        statusMessage: 'No hemos podido enviar el cuaderno.',
        data: [{ body: CueErrorPresenter.formatErrors(body) }]
      });
      return;
    }

    const result = body.resultado;

    if (!result?.codigoResultado) {
      return;
    }

    if (result.codigoResultado === 'FALLO') {
      this.userWarnings = [];
      this.setMessage('error', {
        statusCode: HttpStatusCode.UnprocessableEntity,
        statusMessage: 'La administración ha rechazado el CUE.',
        data: [{ body: CueErrorPresenter.formatDetalleImportacion(result.detalleImportacion) }]
      });
      return;
    }

    if (result.codigoResultado !== 'OK') {
      this.setMessage('warn', {
        statusCode: HttpStatusCode.Ok,
        statusMessage: 'El cuaderno ha devuelto un estado no reconocido.',
        data: [{ body: 'La administración ha devuelto una respuesta que la aplicación no reconoce. Contacta con soporte si el problema continúa.' }]
      });
      return;
    }

    this.userWarnings = [];
    this.setMessage('success', {
      statusCode: HttpStatusCode.Ok,
      statusMessage: 'El cuaderno se ha enviado.',
      data: [
        {
          body: 'Ahora la administración lo está revisando. Puedes dejar esta ventana abierta o volver más tarde.'
        }
      ]
    });

    const detail = Array.isArray(result.detalleImportacion) ? result.detalleImportacion : [];
    const code = detail[0]?.codigo;

    if (!code) {
      this.checkStatus(finca, refreshRequested);
      refreshRequested();
      return;
    }

    this.showSpinner = true;
    await this.pollCueVerification(code, finca, refreshRequested);
  }

  private async pollCueVerification(code: string, finca: FincasModel, refreshRequested: () => void): Promise<void> {
    if (this.activeCuePollCode === code) {
      return;
    }

    const currentPollRunId = this.pollRunId + 1;

    this.pollRunId = currentPollRunId;
    this.activeCuePollCode = code;

    try {
      for (let attempt = 0; attempt < CUE_MAX_POLL_ATTEMPTS; attempt++) {
        if (!this.isCuePollingActive(currentPollRunId)) {
          return;
        }

        const loopResponse = await firstValueFrom(this.cueExportService.syncStatus(finca, code));

        if (!this.isCuePollingActive(currentPollRunId)) {
          return;
        }

        const status = loopResponse.data?.estado ?? 'PENDIENTE';

        if (CueStatusPresenter.isPendingStatus(status)) {
          this.messageState = 'warn';
          this.importResult = {
            statusCode: HttpStatusCode.Ok,
            statusMessage: 'El cuaderno está enviado y pendiente de respuesta.',
            data: [
              {
                body: 'Seguiremos comprobando la respuesta mientras esta ventana siga abierta.'
              }
            ]
          };
          this.showSpinner = true;
          await this.delayCuePolling();

          if (!this.isCuePollingActive(currentPollRunId)) {
            return;
          }

          continue;
        }

        this.lastPendingPollLimitReachedCode = null;
        this.applyFinalCueStatus(loopResponse);
        this.checkStatus(finca, refreshRequested);
        refreshRequested();
        return;
      }

      this.lastPendingPollLimitReachedCode = code;
      this.setMessage('warn', {
        statusCode: HttpStatusCode.Ok,
        statusMessage: 'La administración todavía no ha devuelto el resultado final.',
        data: [
          {
            body: 'El cuaderno queda pendiente. Puedes volver a abrir esta ventana para consultar el estado más tarde.'
          }
        ]
      });
      this.checkStatus(finca, refreshRequested);
    } catch (error) {
      console.error('Error polling CUE status', error);
      this.setMessage('error', {
        statusCode: HttpStatusCode.InternalServerError,
        statusMessage: 'No se ha podido consultar la respuesta final del cuaderno.',
        data: [
          {
            body: 'El envío queda pendiente. Vuelve a consultarlo más tarde o contacta con administración.'
          }
        ]
      });
    } finally {
      if (this.pollRunId === currentPollRunId) {
        this.activeCuePollCode = null;
      }
    }
  }

  private isCuePollingActive(pollRunId: number): boolean {
    return this.pollRunId === pollRunId;
  }

  private cancelCuePolling(): void {
    this.pollRunId += 1;
    this.activeCuePollCode = null;
    this.showSpinner = false;
  }

  private syncLatestPendingCue(finca: FincasModel, refreshRequested: () => void): void {
    const petitionCode = this.statusCue ? CueHistoryPresenter.historyCode(this.statusCue) : '';
    const status = this.statusCue?.estado ?? '';

    if (!petitionCode || petitionCode === '-' || !CueStatusPresenter.isPendingStatus(status)) {
      return;
    }

    if (this.activeCuePollCode === petitionCode || this.lastPendingPollLimitReachedCode === petitionCode) {
      return;
    }

    this.showSpinner = true;
    void this.pollCueVerification(petitionCode, finca, refreshRequested);
  }

  private applyFinalCueStatus(response: CueSyncResponse): void {
    const status = response.data?.estado ?? 'FALLO';
    const acceptedCount = response.data?.accepted_count ?? 0;
    const rejectedCount = response.data?.rejected_count ?? 0;
    const activities = response.data?.activity_summary ?? [];

    if (status === 'OK') {
      this.userWarnings = [];
      this.setMessage('success', {
        statusCode: HttpStatusCode.Ok,
        statusMessage: 'La administración ha aceptado todo el cuaderno.',
        data: [{ body: 'Actividades aceptadas: ' + acceptedCount + '. No tienes que hacer nada más.' }]
      });
      return;
    }

    if (status === 'PARCIAL') {
      this.userWarnings = CueErrorPresenter.warningsFromRejectedActivities(activities);
      this.setMessage('warn', {
        statusCode: HttpStatusCode.Ok,
        statusMessage: 'La administración ha aceptado parte del cuaderno.',
        data: [
          {
            body: this.buildCuePartialSummary(activities, acceptedCount, rejectedCount) + this.buildRetryAdvice()
          }
        ]
      });
      return;
    }

    this.userWarnings = CueErrorPresenter.warningsFromRejectedActivities(activities);
    this.setMessage('error', {
      statusCode: HttpStatusCode.UnprocessableEntity,
      statusMessage: 'La administración no ha aceptado el cuaderno.',
      data: [
        {
          body: this.buildCuePartialSummary(activities, acceptedCount, rejectedCount) + this.buildRetryAdvice()
        }
      ]
    });
  }

  private buildRetryAdvice(): string {
    return '\n\nLas actividades aceptadas no se volverán a enviar.'
      + '\nCorrige las actividades con problemas y reintenta solo las pendientes.';
  }

  private buildCuePartialSummary(activities: CueSyncActivity[], acceptedCount: number, rejectedCount: number): string {
    const rejectedDetails = activities
      .filter((activity) => activity.es_valida === false)
      .map((activity) => CueErrorPresenter.formatRejectedActivity(activity))
      .join('\n\n');
    const summary = 'Actividades aceptadas: ' + acceptedCount + '\nActividades con problemas: ' + rejectedCount + '.';

    if (!rejectedDetails) {
      return summary;
    }

    return summary + '\n\n' + rejectedDetails;
  }

  private async delayCuePolling(): Promise<void> {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, CUE_POLL_EVERY_MS);
    });
  }

  private setMessage(state: CueMessageState, payload?: CueMessagePayload): void {
    this.messageState = state;
    this.importResult = payload ?? null;
    this.showSpinner = false;
  }
}
